import Phaser from 'phaser';
import { itemManager } from '../../managers/ItemManager';

export default class ToolSelectionUI extends Phaser.GameObjects.Container {
    constructor(scene, x = 0, y = 0) {
        super(scene, x, y);
        this.scene = scene;
        
        // Debug mode state
        this.debugMode = false;
        
        // UI state
        this.isVisible = false;
        this.hoveredSlot = null;
        
        // UI dimensions
        this.viewportWidth = 256;
        this.viewportHeight = 192;
        
        // Tool selection slots (5 tools in a row)
        // Tool IDs correspond to items.json Tool section indices
        this.toolSlots = [
            { x: 65, y: 85, toolId: 'Tool_2', textureKey: 'tool-axe', name: 'Axe', enabled: false },
            { x: 98, y: 85, toolId: 'Tool_3', textureKey: 'tool-pickaxe', name: 'Pickaxe', enabled: false },
            { x: 131, y: 85, toolId: 'Tool_1', textureKey: 'tool-watering-can', name: 'Watering Can', enabled: true },
            { x: 164, y: 85, toolId: 'Tool_0', textureKey: 'tool-hoe', name: 'Hoe', enabled: true },
            { x: 197, y: 85, toolId: 'Tool_4', textureKey: 'tool-scythe', name: 'Scythe', enabled: true }
        ];
        
        this.slotSize = 27; // Same as ToolUI
        
        this.createUI();
        this.setupInputHandlers();
        
        scene.add.existing(this);
        
        // Set high depth and fix to screen coordinates
        this.setDepth(550);
        this.setScrollFactor(0);
        
        // Start hidden
        this.setVisible(false);
    }
    
    createUI() {
        // Create the main UI background
        this.background = this.scene.add.image(this.viewportWidth / 2, this.viewportHeight / 2, 'tool-selection-ui');
        this.background.setOrigin(0.5);
        this.background.setScrollFactor(0);
        this.background.setDepth(550);
        this.background.setVisible(false);
        
        // Create hover overlay (initially invisible)
        this.hoverOverlay = this.scene.add.image(0, 0, 'tool-ui-icon-empty-hover');
        this.hoverOverlay.setVisible(false);
        this.hoverOverlay.setScrollFactor(0);
        this.hoverOverlay.setDepth(552);
        
        // Create tool icons
        this.toolIcons = [];
        this.toolSlots.forEach((slot, index) => {
            const icon = this.scene.add.image(slot.x, slot.y, slot.textureKey);
            icon.setScrollFactor(0);
            icon.setDepth(553); // Above hover overlay
            icon.setVisible(false);
            
            // Make disabled tools transparent
            if (!slot.enabled) {
                icon.setAlpha(0.3);
            }
            
            this.toolIcons.push(icon);
        });
        
        // Create debug graphics
        this.debugGraphics = this.scene.add.graphics();
        this.debugGraphics.setScrollFactor(0);
        this.debugGraphics.setDepth(560);
        
        // Create interactive areas for tool slots
        this.createSlotInteractiveAreas();
        
        // Create close button
        this.setupCloseButton();
        
        // Setup tooltip
        this.setupTooltip();
    }
    
    createSlotInteractiveAreas() {
        this.slotAreas = [];
        
        this.toolSlots.forEach((slot, index) => {
            // Create invisible interactive rectangle for each slot
            const area = this.scene.add.rectangle(slot.x, slot.y, this.slotSize, this.slotSize, 0x000000, 0);
            area.setInteractive();
            area.setScrollFactor(0);
            area.setDepth(555);
            area.setVisible(false);
            
            // Store slot data
            area.slotIndex = index;
            area.toolData = slot;
            
            // Setup hover events
            area.on('pointerover', () => {
                if (this.isVisible) {
                    this.hoveredSlot = index;
                    
                    // Only show hover overlay for enabled tools
                    if (slot.enabled) {
                        this.hoverOverlay.setPosition(slot.x, slot.y);
                        this.hoverOverlay.setVisible(true);
                    }
                    
                    // Show tooltip for all tools (enabled and disabled)
                    this.updateTooltip(area);
                    this.tooltip.setVisible(true);
                }
            });

            area.on('pointerout', () => {
                this.hoveredSlot = null;
                this.hoverOverlay.setVisible(false);
                this.tooltip.setVisible(false);
            });
            
            // Click handlers
            area.on('pointerdown', (pointer) => {
                if (!this.isVisible) return;
                
                // Ignore clicks on disabled tools
                if (!slot.enabled) {
                    return;
                }
                
                const toolId = slot.toolId;
                
                if (pointer.leftButtonDown()) {
                    // Set blocking flag on the scene to prevent tool actions
                    this.scene.uiInteractionBlocked = true;
                    // Safety timeout to clear the flag
                    this.scene.time.delayedCall(100, () => {
                        this.scene.uiInteractionBlocked = false;
                    });
                    this.equipToolToSlot(toolId, 1);
                } else if (pointer.rightButtonDown()) {
                    pointer.event.preventDefault(); // Prevent browser context menu
                    // Set blocking flag on the scene to prevent tool actions
                    this.scene.uiInteractionBlocked = true;
                    // Safety timeout to clear the flag
                    this.scene.time.delayedCall(100, () => {
                        this.scene.uiInteractionBlocked = false;
                    });
                    this.equipToolToSlot(toolId, 2);
                }
            });
            
            this.slotAreas.push(area);
        });
    }
    
    setupCloseButton() {
        // Add close button (X) positioned for the tool selection UI
        this.closeButton = this.scene.add.text(224, 60, 'X', {
            fontSize: '16px',
            fill: '#ffffff',
            fontStyle: 'bold'
        }).setOrigin(0.5)
          .setInteractive()
          .setDepth(556) // Above all other elements
          .setScrollFactor(0)
          .setVisible(false);

        this.closeButton.on('pointerover', () => this.closeButton.setTint(0xdddddd));
        this.closeButton.on('pointerout', () => this.closeButton.clearTint());
        this.closeButton.on('pointerdown', () => this.closeUI());
    }
    
    setupTooltip() {
        this.tooltip = this.scene.add.container(0, 0);
        this.tooltip.setAlpha(0.8);
        this.tooltip.setDepth(1000);
        this.tooltip.setScrollFactor(0);
        
        this.tooltipBg = this.scene.add.rectangle(0, 0, 155, 40, 0x78716C)
            .setOrigin(0)
            .setStrokeStyle(2, 0xffffff);
        this.tooltip.add(this.tooltipBg);
        
        this.titleText = this.scene.add.text(3, 3, '', { 
            fontSize: '12px',
            fontStyle: 'bold',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.typeText = this.scene.add.text(3, 20, '', {
            fontSize: '12px',
            fontStyle: 'italic',
            fill: '#1C1917'
        });
        
        this.descriptionText = this.scene.add.text(3, 35, '', {
            fontSize: '12px',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.tooltip.add([this.titleText, this.typeText, this.descriptionText]);
        this.tooltip.setVisible(false);
    }
    
    setupInputHandlers() {
        // T key toggles tool selection UI
        this.scene.input.keyboard.on('keydown-T', () => {
            this.toggleVisibility();
        });
        
        // Y key closes tool selection UI (when seedbag is opened)
        this.scene.input.keyboard.on('keydown-Y', () => {
            if (this.isVisible) {
                this.closeUI();
            }
        });

        this.scene.input.keyboard.on('keydown-ESC', async () => {
            if (this.isVisible) {
                this.closeUI();
            }
        });
        
        // X key toggles debug mode
        this.scene.input.keyboard.on('keydown-X', () => {
            this.debugMode = !this.debugMode;
            this.updateDebugVisuals();
        });
    }
    
    toggleVisibility() {
        this.isVisible = !this.isVisible;
        
        // Update visibility of all UI elements
        this.background.setVisible(this.isVisible);
        this.toolIcons.forEach(icon => icon.setVisible(this.isVisible));
        this.slotAreas.forEach(area => area.setVisible(this.isVisible));
        this.closeButton.setVisible(this.isVisible);
        
        // Hide hover overlay when closing
        if (!this.isVisible) {
            this.hoverOverlay.setVisible(false);
            this.hoveredSlot = null;
        }
        
        this.updateDebugVisuals();
    }
    
    closeUI() {
        if (this.isVisible) {
            this.isVisible = false;
            
            // Update visibility of all UI elements
            this.background.setVisible(false);
            this.toolIcons.forEach(icon => icon.setVisible(false));
            this.slotAreas.forEach(area => area.setVisible(false));
            this.closeButton.setVisible(false);
            
            // Hide hover overlay
            this.hoverOverlay.setVisible(false);
            this.hoveredSlot = null;
            
            this.updateDebugVisuals();
        }
    }
    
    updateDebugVisuals() {
        this.debugGraphics.clear();
        
        if (this.debugMode && this.isVisible) {
            // Draw debug rectangles over tool slots
            this.debugGraphics.lineStyle(2, 0x00ff00); // Green border
            this.debugGraphics.fillStyle(0x00ff00, 0.3); // Green fill with transparency
            
            this.toolSlots.forEach((slot, index) => {
                const rect = new Phaser.Geom.Rectangle(
                    slot.x - this.slotSize / 2,
                    slot.y - this.slotSize / 2,
                    this.slotSize,
                    this.slotSize
                );
                this.debugGraphics.strokeRectShape(rect);
                this.debugGraphics.fillRectShape(rect);
            });
            
            // Add debug text
            if (!this.debugText) {
                this.debugText = this.scene.add.text(10, 50, '', {
                    fontFamily: 'Arial',
                    fontSize: '12px',
                    color: '#00ff00'
                });
                this.debugText.setScrollFactor(0);
                this.debugText.setDepth(600);
            }
            
            this.debugText.setText([
                'Tool Selection UI Debug Mode',
                `Visible: ${this.isVisible}`,
                `Hovered: ${this.hoveredSlot !== null ? this.toolSlots[this.hoveredSlot].name : 'none'}`,
                'Left Click: Assign to Slot 1',
                'Right Click: Assign to Slot 2',
                'Tools: ' + this.toolSlots.map(slot => slot.name).join(', ')
            ]);
            this.debugText.setVisible(true);
        } else {
            if (this.debugText) {
                this.debugText.setVisible(false);
            }
        }
    }
    
    destroy() {
        // Clean up all created objects
        if (this.background) this.background.destroy();
        if (this.hoverOverlay) this.hoverOverlay.destroy();
        if (this.debugText) this.debugText.destroy();
        if (this.debugGraphics) this.debugGraphics.destroy();
        
        this.toolIcons.forEach(icon => icon.destroy());
        this.slotAreas.forEach(area => area.destroy());
        if (this.closeButton) this.closeButton.destroy();
        
        super.destroy();
    }
    
    // Equip tool to specified slot
    async equipToolToSlot(toolId, slotNumber) {
        try {
            // Update the ToolUI display
            if (this.scene.toolUI) {
                this.scene.toolUI.updateEquippedTool(slotNumber, toolId);
            }
            
            // Make API call to save equipment state
            await this.saveEquipmentState(slotNumber, toolId);
            
        } catch (error) {
            console.error('Failed to equip tool:', error);
        }
    }
    
    // Save equipment state to backend
    async saveEquipmentState(slotNumber, itemCode) {
        try {
            const gameService = window.gameService;
            if (!gameService) {
                console.warn('GameService not available, cannot save equipment state');
                return;
            }

            // Create update object with only the relevant slot
            const updateData = {};
            if (slotNumber === 1) {
                updateData.toolslot_1_equipped = itemCode;
            } else if (slotNumber === 2) {
                updateData.toolslot_2_equipped = itemCode;
            }

            // Update user options
            await gameService.updateUserOptions(updateData);
            
        } catch (error) {
            console.error('Error saving equipment state:', error);
        }
    }
    
    // Update tooltip content
    updateTooltip(area) {
        const slot = area.toolData;
        
        this.titleText.setText(slot.name);
        this.titleText.setColor('#ffffff');
        this.typeText.setText('Tool');
        
        // Show different description for disabled tools
        if (!slot.enabled) {
            this.descriptionText.setText('Coming Soon! This tool is not available yet.');
            this.descriptionText.setFontStyle("italic");
            this.titleText.setColor('#888888'); // Gray out the title
        } else {
            // Get tool description from items.json if available
            const toolData = itemManager.getItemData(slot.toolId);
            const description = toolData ? toolData.description : 'A useful tool for various tasks.';
            this.descriptionText.setText(description);
            this.descriptionText.setFontStyle("italic");
        }
        
        // Calculate tooltip dimensions and position
        let totalHeight = 3 + 
            this.titleText.height + 
            this.typeText.height +
            this.descriptionText.height + 
            12;
        
        const largestWidth = Math.max(
            this.titleText.width, 
            this.typeText.width, 
            this.descriptionText.width
        );

        this.tooltipBg.setSize(largestWidth + 6, totalHeight);

        const UI_WIDTH = 256;
        const UI_HEIGHT = 192;
        const TOOLTIP_MARGIN = 5;

        let tooltipX = slot.x + 16;
        let tooltipY = slot.y + 16;

        // Adjust horizontal position
        if (tooltipX + largestWidth > UI_WIDTH) {
            tooltipX = slot.x - largestWidth - TOOLTIP_MARGIN;
        }

        if (tooltipX < 0) {
            tooltipX = TOOLTIP_MARGIN;
        }

        // Adjust vertical position
        if (tooltipY + totalHeight > UI_HEIGHT) {
            tooltipY = slot.y - totalHeight - TOOLTIP_MARGIN;
        }

        if (tooltipY < 0) {
            tooltipY = TOOLTIP_MARGIN;
        }

        this.tooltip.setPosition(tooltipX, tooltipY);
    }
} 